import * as stylex from "@stylexjs/stylex";
import {
  colors,
  fonts,
  fontSizes,
  spacing,
} from "@softmax/design-system/tokens.stylex";
import type { StorePlayerBuildSample } from "./play-diagram-records";

const indentStyles = stylex.create({
  0: { paddingLeft: 0 },
  1: { paddingLeft: spacing.space8 },
  2: { paddingLeft: spacing.space16 },
});

export function PlayerBuildCard({
  sample,
  fig,
}: {
  readonly sample: StorePlayerBuildSample;
  readonly fig: "a" | "b";
}) {
  return (
    <div {...stylex.props(styles.build)}>
      <div {...stylex.props(styles.human)}>
        <p {...stylex.props(styles.humanLabel)}>You</p>
        <p {...stylex.props(styles.humanLine)}>
          &ldquo;{sample.instruction}&rdquo;
        </p>
      </div>
      <div {...stylex.props(styles.terminal)}>
        {fig === "b" ? (
          <>
            <p {...stylex.props(styles.terminalText, styles.terminalLabel)}>
              AI assistant » writing
            </p>
            <div aria-hidden="true" {...stylex.props(styles.chipRule)} />
          </>
        ) : null}
        <p
          {...stylex.props(
            styles.terminalText,
            styles.terminalLabel,
            styles.filename,
          )}
        >
          {fig === "b" ? "$ cat " : ""}
          {sample.filename}
        </p>
        <div {...stylex.props(styles.code)}>
          {sample.code.map((line, index) => (
            <p
              key={index}
              data-code-line="true"
              {...stylex.props(
                styles.terminalText,
                styles.codeLine,
                indentStyles[line.depth],
              )}
            >
              {line.text}
            </p>
          ))}
        </div>
      </div>
    </div>
  );
}

const styles = stylex.create({
  build: {
    display: "flex",
    width: "100%",
    height: "100%",
    minHeight: 0,
    flexDirection: "column",
    gap: {
      default: spacing.space4,
      "@container (min-width: 560px)": spacing.space6,
    },
  },
  human: { flexShrink: 0 },
  humanLabel: {
    margin: 0,
    fontFamily: fonts.sans,
    fontSize: fontSizes.caption,
    lineHeight: "16px",
    color: colors.muted,
  },
  humanLine: {
    margin: 0,
    fontFamily: fonts.sans,
    fontSize: fontSizes.label,
    lineHeight: 1.35,
    color: colors.foreground,
  },
  terminal: {
    display: "flex",
    minHeight: 0,
    flex: 1,
    flexDirection: "column",
    gap: 1,
    overflow: "hidden",
    borderRadius: 3,
    borderWidth: 1,
    borderStyle: "solid",
    borderColor: colors.terminalMuted,
    backgroundColor: colors.terminalSurface,
    paddingInline: {
      default: spacing.space10,
      "@container (min-width: 700px)": spacing.space12,
    },
    paddingBlock: {
      default: spacing.space6,
      "@container (min-width: 700px)": spacing.space8,
    },
  },
  terminalText: {
    margin: 0,
    fontFamily: fonts.mono,
    fontSize: {
      default: fontSizes.small,
      "@container (min-width: 560px)": fontSizes.caption,
    },
    lineHeight: { default: "18px", "@container (min-width: 560px)": "16px" },
  },
  terminalLabel: { color: colors.terminalMuted },
  filename: {
    display: "-webkit-box",
    WebkitBoxOrient: "vertical",
    WebkitLineClamp: 2,
    overflow: "hidden",
    overflowWrap: "anywhere",
  },
  chipRule: {
    marginBlock: 1,
    height: 1,
    flexShrink: 0,
    width: "100%",
    backgroundImage: `repeating-linear-gradient(90deg, ${colors.terminalMuted} 0 3px, transparent 3px 6px)`,
  },
  code: { minHeight: 0, flex: 1, overflow: "hidden" },
  codeLine: {
    whiteSpace: "pre-wrap",
    overflowWrap: "anywhere",
    color: colors.terminalText,
  },
});
